// DetalleConsulta.js - Rediseño Moderno (moustomp)
import React, { useState, useEffect } from 'react';
import {
  Typography, Box, TextField, Button, Tooltip, IconButton,
  Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel, FormGroup, Select, FormControl, InputLabel,
  Paper, Chip, Divider,
} from '@mui/material';
import {
  MedicalServices as MedicalServicesIcon,
  Science as ScienceIcon,
  Notes as NotesIcon,
  LocalHospital as LocalHospitalIcon,
  Event as EventIcon,
  Create as CreateIcon,
  Update as UpdateIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { TextareaAutosize } from '@mui/material';
import api from '../api/api';

// ─── Paleta ───────────────────────────────────────────────────────────────────
const THEME = {
  primary: '#1a6b4a',
  primaryLight: '#e8f5ee',
  primaryMid: '#2e8b62',
  accent: '#f0a500',
  accentLight: '#fff8e6',
  danger: '#d64045',
  dangerLight: '#fdecea',
  surface: '#ffffff',
  surfaceAlt: '#f7f9f8',
  border: '#e0ebe6',
  borderDark: '#c5d9d0',
  textPrimary: '#0f2d1e',
  textSecondary: '#5a7a6e',
  textMuted: '#93b0a5',
  shadow: '0 2px 12px rgba(26,107,74,0.08)',
  shadowMd: '0 4px 24px rgba(26,107,74,0.12)',
};

// ─── Subcomponentes ───────────────────────────────────────────────────────────

const SectionCard = ({ icon: Icon, title, color = THEME.primary, children }) => (
  <Paper elevation={0} sx={{ border: `1px solid ${THEME.border}`, borderRadius: '14px', overflow: 'hidden', mb: 2 }}>
    <Box sx={{
      px: 2.5, py: 1.5, bgcolor: THEME.surface,
      borderBottom: `1px solid ${THEME.border}`,
      display: 'flex', alignItems: 'center', gap: 1,
    }}>
      <Icon sx={{ fontSize: 15, color }} />
      <Typography sx={{ fontSize: '12px', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2.5, bgcolor: THEME.surface }}>
      {children}
    </Box>
  </Paper>
);

const InfoCell = ({ label, value, icon: Icon, editing, children }) => (
  <Box sx={{
    p: '10px 12px',
    border: `1px solid ${THEME.border}`,
    borderRadius: '10px',
    bgcolor: THEME.surfaceAlt,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
      {Icon && <Icon sx={{ fontSize: 12, color: THEME.textMuted }} />}
      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
    </Box>
    {editing ? children : (
      <Typography sx={{ fontSize: '13.5px', color: value ? THEME.textPrimary : THEME.textMuted, fontStyle: value ? 'normal' : 'italic', lineHeight: 1.4 }}>
        {value || 'Sin datos'}
      </Typography>
    )}
  </Box>
);

const TextBlock = ({ label, value, editing, rows = 4, onChange, extra }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.6 }}>
      {label}
    </Typography>
    {editing ? (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <TextField
          fullWidth multiline rows={rows}
          minRows={rows}
          maxRows={20}
          value={value} onChange={onChange}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: '13.5px',
              '&:hover fieldset': { borderColor: THEME.primaryMid },
              '&.Mui-focused fieldset': { borderColor: THEME.primary, borderWidth: '2px' },
            },
            '& .MuiInputBase-inputMultiline': {
              resize: 'vertical',           // permite al usuario ajustar manualmente
            }
          }}
        />
        {extra}
      </Box>
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{
          fontSize: '13.5px', color: value ? THEME.textPrimary : THEME.textMuted,
          fontStyle: value ? 'normal' : 'italic', whiteSpace: 'pre-wrap', lineHeight: 1.6, flex: 1,
        }}>
          {value || 'Sin datos registrados'}
        </Typography>
        {extra}
      </Box>
    )}
  </Box>
);

const FIELD_LABELS = {
  frecuencia_respiratoria: 'Frec. Respiratoria',
  frecuencia_cardiaca: 'Frec. Cardiaca',
  pulso: 'Pulso',
  mucosas: 'Mucosas',
  deshidratacion: 'Deshidratación',
  temperatura: 'Temperatura',
  peso: 'Peso',
};

// ─── Componente principal ─────────────────────────────────────────────────────
const DetalleConsulta = ({
  selectedCita, citas, setCitas,
  id_paciente, pacienteNombre, razaNombre,
  onRefresh, setError,
}) => {
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [plantillaHistorial, setPlantillaHistorial] = useState(1);
  const [openSelector, setOpenSelector] = useState(false);
  const [selectedConsultas, setSelectedConsultas] = useState([]);
  const [todasConsultas, setTodasConsultas] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const normalizeConsulta = (consulta, id_cita) => {
    if (!consulta) return {
      id_consulta: null, id_cita: id_cita || null,
      frecuencia_respiratoria: '', frecuencia_cardiaca: '', pulso: '',
      mucosas: '', deshidratacion: '', temperatura: '', peso: '',
      diagnostico_patologia: '', tratamiento: '', tratamiento_clinica: '',
      sintomas: '', observaciones: '', historial_clinico: '',
      createdAt: null, updatedAt: null,
    };
    return {
      id_consulta: consulta.id_consulta ?? null,
      id_cita: consulta.id_cita ?? id_cita ?? null,
      frecuencia_respiratoria: consulta.frecuencia_respiratoria ?? '',
      frecuencia_cardiaca: consulta.frecuencia_cardiaca ?? '',
      pulso: consulta.pulso ?? '',
      mucosas: consulta.mucosas ?? '',
      deshidratacion: consulta.deshidratacion ?? '',
      temperatura: consulta.temperatura ?? '',
      peso: consulta.peso ?? '',
      diagnostico_patologia: consulta.diagnostico_patologia ?? '',
      tratamiento: consulta.tratamiento ?? '',
      tratamiento_clinica: consulta.tratamiento_clinica ?? '',
      sintomas: consulta.sintomas ?? '',
      observaciones: consulta.observaciones ?? '',
      historial_clinico: consulta.historial_clinico ?? '',
      createdAt: consulta.createdAt ?? null,
      updatedAt: consulta.updatedAt ?? null,
    };
  };

  const formatDate = (ts) => {
    if (!ts || ts <= 0) return 'Sin fecha';
    return new Date(ts).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  useEffect(() => {
    if (selectedCita) {
      setFormData({ ...normalizeConsulta(selectedCita.consulta, selectedCita.id_cita), razon_consulta: selectedCita.razon_consulta || '' });
      setIsEditing(false);
    }
    if (id_paciente) {
      api.get(`/consultas/paciente/${id_paciente}`).then(res => setTodasConsultas(res.data)).catch(console.error);
    }
  }, [selectedCita, id_paciente]);

  // ── Descarga ──
  const descargarConsultaPDF = async (id_consulta, template = 1) => {
    if (!id_consulta) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('token') || '';
      const baseUrl = process.env.REACT_APP_API_URL || 'https://villademio.cloud/api';
      const response = await fetch(`${baseUrl}/consultas/imprimir/consulta/${id_consulta}?template=${template}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `consulta_${id_consulta}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert('Error: ' + err.message); }
    finally { setDownloading(false); }
  };

  const descargarHistorialCompleto = async (id_paciente, template = 1) => {
    if (!id_paciente) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${process.env.REACT_APP_API_URL || 'https://villademio.cloud/api'}/consultas/imprimir/paciente/${id_paciente}?template=${template}`;
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Error al generar historial');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = blobUrl; a.download = 'historial_completo.pdf'; a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) { alert('Error: ' + err.message); }
    finally { setDownloading(false); }
  };

  const descargarHistorialSeleccionado = async () => {
    if (!selectedConsultas.length) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${process.env.REACT_APP_API_URL || 'https://villademio.cloud/api'}/consultas/imprimir/paciente/${id_paciente}/seleccionadas`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ consultas_ids: selectedConsultas, template: plantillaHistorial }),
      });
      if (!response.ok) throw new Error('Error al generar PDF');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = blobUrl; a.download = 'historial_seleccionado.pdf'; a.click();
      window.URL.revokeObjectURL(blobUrl);
      setOpenSelector(false);
    } catch (err) { alert('Error: ' + err.message); }
    finally { setDownloading(false); }
  };

  // ── CRUD ──
  const handleEditConsulta = async () => {
    try {
      if (!selectedCita) return;
      const citaPayload = {};
      const consultaPayload = {};
      if (formData.razon_consulta !== selectedCita.razon_consulta) citaPayload.razon_consulta = formData.razon_consulta || null;
      const fields = ['frecuencia_respiratoria','frecuencia_cardiaca','pulso','mucosas','deshidratacion','temperatura','peso','diagnostico_patologia','tratamiento','tratamiento_clinica','sintomas','observaciones','historial_clinico'];
      fields.forEach(f => {
        const orig = selectedCita.consulta ? selectedCita.consulta[f] : '';
        if (formData[f] !== orig) consultaPayload[f] = formData[f] || null;
      });
      if (Object.keys(consultaPayload).length > 0) citaPayload.fecha_efectiva = new Date().toISOString();
      let updatedCita = selectedCita, updatedConsulta = selectedCita.consulta;
      if (Object.keys(citaPayload).length > 0) { const r = await api.put(`/citas/${selectedCita.id_cita}`, citaPayload); updatedCita = r.data; }
      if (Object.keys(consultaPayload).length > 0) {
        if (selectedCita.consulta?.id_consulta) { const r = await api.put(`/consultas/${selectedCita.consulta.id_consulta}`, consultaPayload); updatedConsulta = r.data; }
        else { const r = await api.post('/consultas/', { ...consultaPayload, id_paciente, id_cita: selectedCita.id_cita }); updatedConsulta = r.data; }
      }
      setCitas(citas.map(c => c.id_cita === selectedCita.id_cita ? { ...updatedCita, consulta: updatedConsulta } : c));
      setFormData({ ...normalizeConsulta(updatedConsulta), razon_consulta: updatedCita.razon_consulta || '' });
      setIsEditing(false); setError(null);
      await onRefresh();
    } catch (err) { setError('Error al editar: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleDeleteConsulta = async () => {
    if (!window.confirm('¿Estás seguro de eliminar esta consulta?')) return;
    try {
      const consultaId = selectedCita.consulta?.id_consulta;
      if (!consultaId) throw new Error('No hay consulta para eliminar');
      await api.delete(`/consultas/${consultaId}`);
      setCitas(citas.filter(c => c.id_cita !== selectedCita.id_cita));
      await onRefresh();
    } catch (err) { setError('Error al eliminar: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleCopyTratamiento = () => {
    if (formData.tratamiento) navigator.clipboard.writeText(formData.tratamiento).catch(() => setError('Error al copiar'));
    else alert('No hay tratamiento para copiar');
  };

  const fd = (f) => formData[f] || '';
  const setFd = (f) => (e) => setFormData({ ...formData, [f]: e.target.value });

  if (!selectedCita) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── TOOLBAR ── */}
      <Box sx={{
        px: 3, py: 1.5, bgcolor: THEME.surface,
        borderBottom: `1px solid ${THEME.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 800, color: THEME.textPrimary, lineHeight: 1.2 }}>
              {selectedCita.fecha_cita
                ? new Date(selectedCita.fecha_cita).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })
                : 'Sin fecha'}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>
              {fd('razon_consulta') || 'Sin motivo registrado'}
            </Typography>
          </Box>
          {selectedCita.consulta && (
            <Chip
              label="Con consulta"
              size="small"
              sx={{ height: 20, fontSize: '10px', fontWeight: 700, bgcolor: THEME.primaryLight, color: THEME.primary }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Selector plantilla + descarga */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ fontSize: '12px' }}>Plantilla</InputLabel>
            <Select
              value={plantillaHistorial}
              label="Plantilla"
              onChange={(e) => setPlantillaHistorial(e.target.value)}
              disabled={downloading}
              sx={{ fontSize: '12px', borderRadius: '9px' }}
            >
              <MenuItem value={1}>Tabla Clásica</MenuItem>
              <MenuItem value={2}>Secciones</MenuItem>
              <MenuItem value={3}>Moderna</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title={downloading ? 'Generando PDF...' : 'Opciones de descarga'}>
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              disabled={downloading}
              sx={{ color: THEME.textSecondary, '&:hover': { color: THEME.primary, bgcolor: THEME.primaryLight } }}
            >
              {downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { borderRadius: '12px', boxShadow: THEME.shadowMd, border: `1px solid ${THEME.border}` } }}>
            {selectedCita.consulta && (
              <MenuItem onClick={() => { setAnchorEl(null); descargarConsultaPDF(selectedCita.consulta.id_consulta, plantillaHistorial); }}
                sx={{ fontSize: '13px', gap: 1 }}>
                <DownloadIcon fontSize="small" sx={{ color: THEME.primary }} /> Consulta actual
              </MenuItem>
            )}
            <MenuItem onClick={() => { setAnchorEl(null); descargarHistorialCompleto(id_paciente, plantillaHistorial); }}
              sx={{ fontSize: '13px', gap: 1 }}>
              <DownloadIcon fontSize="small" sx={{ color: THEME.primary }} /> Historial completo
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); setSelectedConsultas([]); setOpenSelector(true); }}
              sx={{ fontSize: '13px', gap: 1 }}>
              <FilterListIcon fontSize="small" sx={{ color: THEME.primary }} /> Historial personalizado...
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ── CONTENIDO SCROLLEABLE ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>

        {/* Información General */}
        <SectionCard icon={MedicalServicesIcon} title="Información General">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
            <InfoCell label="Razón de consulta" value={fd('razon_consulta')} icon={NotesIcon} editing={isEditing}>
              <TextField fullWidth size="small" value={fd('razon_consulta')} onChange={setFd('razon_consulta')}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </InfoCell>
            <InfoCell label="ID Cita" value={String(selectedCita.id_cita)} icon={NotesIcon} />
            <InfoCell label="Fecha Efectiva" icon={EventIcon}
              value={selectedCita.fecha_efectiva ? new Date(selectedCita.fecha_efectiva).toLocaleString('es-ES') : null} />
            {selectedCita.consulta?.createdAt && (
              <InfoCell label="Creado" icon={CreateIcon} value={formatDate(selectedCita.consulta.createdAt)} />
            )}
            {selectedCita.consulta?.updatedAt && (
              <InfoCell label="Actualizado" icon={UpdateIcon} value={formatDate(selectedCita.consulta.updatedAt)} />
            )}
          </Box>
        </SectionCard>

        {/* Fisiología */}
        <SectionCard icon={ScienceIcon} title="Fisiología y Peso">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            {Object.entries(FIELD_LABELS).map(([field, label]) => (
              <InfoCell key={field} label={label} value={fd(field)} editing={isEditing}>
                <TextField fullWidth size="small" value={fd(field)} onChange={setFd(field)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
              </InfoCell>
            ))}
          </Box>
        </SectionCard>

        {/* Historial y Síntomas */}
        <SectionCard icon={NotesIcon} title="Historial y Síntomas">
          <TextBlock label="Síntomas" value={fd('sintomas')} editing={isEditing} onChange={setFd('sintomas')} />
          <TextBlock label="Historial Clínico" value={fd('historial_clinico')} editing={isEditing} rows={5} onChange={setFd('historial_clinico')} />
          <TextBlock label="Observaciones" value={fd('observaciones')} editing={isEditing} onChange={setFd('observaciones')} />
        </SectionCard>

        {/* Diagnóstico y Tratamiento */}
        <SectionCard icon={LocalHospitalIcon} title="Diagnóstico y Tratamiento">
          <TextBlock label="Diagnóstico / Patología" value={fd('diagnostico_patologia')} editing={isEditing} onChange={setFd('diagnostico_patologia')} />
          <TextBlock label="Tratamiento en clínica" value={fd('tratamiento_clinica')} editing={isEditing} onChange={setFd('tratamiento_clinica')} rows={4}/>
          <TextBlock
            label="Tratamiento domiciliario"
            value={fd('tratamiento')}
            editing={isEditing}
            onChange={setFd('tratamiento')}
            extra={
              <Tooltip title="Copiar tratamiento">
                <IconButton size="small" onClick={handleCopyTratamiento}
                  sx={{ color: THEME.textMuted, '&:hover': { color: THEME.primary, bgcolor: THEME.primaryLight } }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
        </SectionCard>

      </Box>

      {/* ── ACTION BAR FIJA ── */}
      <Box sx={{
        px: 3, py: 1.5,
        bgcolor: THEME.surfaceAlt,
        borderTop: `1px solid ${THEME.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1,
        flexShrink: 0,
      }}>
        {!isEditing ? (
          <>
            {selectedCita.consulta && (
              <Button
                size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                onClick={handleDeleteConsulta}
                sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12px', borderColor: THEME.dangerLight, color: THEME.danger, '&:hover': { bgcolor: THEME.dangerLight } }}
              >
                Eliminar
              </Button>
            )}
            <Button
              size="small" variant="contained" startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12px', fontWeight: 700, bgcolor: THEME.primary, '&:hover': { bgcolor: THEME.primaryMid }, boxShadow: 'none' }}
            >
              Editar
            </Button>
          </>
        ) : (
          <>
            <Button size="small" variant="text" onClick={() => setIsEditing(false)}
              sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12px', color: THEME.textSecondary }}>
              Cancelar
            </Button>
            <Button
              size="small" variant="contained" startIcon={<SaveIcon />}
              onClick={handleEditConsulta}
              sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12px', fontWeight: 700, bgcolor: THEME.primary, '&:hover': { bgcolor: THEME.primaryMid }, boxShadow: 'none' }}
            >
              Guardar Cambios
            </Button>
          </>
        )}
      </Box>

      {/* ── DIALOG HISTORIAL PERSONALIZADO ── */}
      <Dialog open={openSelector} onClose={() => setOpenSelector(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: THEME.textPrimary }}>
          Selecciona consultas para el PDF
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: THEME.border }}>
          <FormGroup>
            {todasConsultas.map((c) => (
              <FormControlLabel
                key={c.id_consulta}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedConsultas.includes(c.id_consulta)}
                    onChange={(e) => setSelectedConsultas(prev =>
                      e.target.checked ? [...prev, c.id_consulta] : prev.filter(id => id !== c.id_consulta)
                    )}
                    sx={{ color: THEME.primary, '&.Mui-checked': { color: THEME.primary } }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '13px', color: THEME.textPrimary }}>
                    #{c.id_consulta} — {formatDate(c.updatedAt)} — {typeof c.diagnostico === 'string' ? c.diagnostico.slice(0, 40) : 'Sin diagnóstico'}
                  </Typography>
                }
                sx={{ mb: 0.5 }}
              />
            ))}
          </FormGroup>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Plantilla</InputLabel>
            <Select value={plantillaHistorial} onChange={(e) => setPlantillaHistorial(Number(e.target.value))} label="Plantilla"
              sx={{ borderRadius: '10px' }}>
              <MenuItem value={1}>Tabla Clásica</MenuItem>
              <MenuItem value={2}>Secciones Detalladas</MenuItem>
              <MenuItem value={3}>Moderna</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          <Button onClick={() => setOpenSelector(false)} size="small"
            sx={{ borderRadius: '8px', textTransform: 'none', color: THEME.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained" size="small"
            onClick={descargarHistorialSeleccionado}
            disabled={selectedConsultas.length === 0 || downloading}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: THEME.primary, '&:hover': { bgcolor: THEME.primaryMid }, boxShadow: 'none' }}
          >
            {downloading ? 'Generando...' : `Descargar (${selectedConsultas.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DetalleConsulta;